import { useFetchWrapper } from '_helpers';

export { useTuitionActions };

function useTuitionActions() {
    const fetchWrapper = useFetchWrapper();

    async function getAllTuition() {
        try {
            const res = await fetchWrapper.get('/api/tuition');
            const json = await res.json();
            if (json.status === 'Success') return json.message;
            return [];
        } catch (e) {
            console.error('getAllTuition:', e);
            return [];
        }
    }

    // Lazy-load one student's full tuition detail (used when a row is expanded).
    // Returns the whole enriched object: { student_ref, records, total_paid,
    // packages, total_quota, lessons_used, lessons_remaining, warning }.
    async function getStudentTuition(student_id) {
        try {
            const res = await fetchWrapper.get(`/api/tuition?student_id=${encodeURIComponent(student_id)}`);
            const json = await res.json();
            if (json.status === 'Success') return json.message?.[0] || null;
            return null;
        } catch (e) {
            console.error('getStudentTuition:', e);
            return null;
        }
    }

    async function addTuitionRecord({ student_id, payment_date, amount, payment_method, notes }) {
        const body = new URLSearchParams();
        body.append('student_id',     student_id);
        body.append('payment_date',   payment_date);
        body.append('amount',         amount);
        body.append('payment_method', payment_method);
        body.append('notes',          notes || '');
        const res = await fetchWrapper.post('/api/tuition/add', 'application/x-www-form-urlencoded', body);
        return res.json();
    }

    async function updateTuitionRecord(recordId, { payment_date, amount, payment_method, notes }) {
        const body = new URLSearchParams();
        if (payment_date   !== undefined) body.append('payment_date',   payment_date);
        if (amount         !== undefined) body.append('amount',         amount);
        if (payment_method !== undefined) body.append('payment_method', payment_method);
        if (notes          !== undefined) body.append('notes',          notes);
        const res = await fetchWrapper.put(`/api/tuition/${recordId}`, 'application/x-www-form-urlencoded', body);
        return res.json();
    }

    async function deleteTuitionRecord(recordId) {
        const res = await fetchWrapper.delete(`/api/tuition/${recordId}`);
        return res.json();
    }

    async function addTuitionPackage({ student_id, lesson_quota, price, notes }) {
        const body = new URLSearchParams();
        body.append('student_id',   student_id);
        body.append('lesson_quota', lesson_quota);
        body.append('price',        price ?? 0);
        body.append('notes',        notes || '');
        const res = await fetchWrapper.post('/api/tuition/packages', 'application/x-www-form-urlencoded', body);
        return res.json();
    }

    async function updateTuitionPackage(packageId, { lesson_quota, price, notes }) {
        const body = new URLSearchParams();
        if (lesson_quota !== undefined) body.append('lesson_quota', lesson_quota);
        if (price        !== undefined) body.append('price',        price);
        if (notes        !== undefined) body.append('notes',        notes);
        const res = await fetchWrapper.put(`/api/tuition/packages/${packageId}`, 'application/x-www-form-urlencoded', body);
        return res.json();
    }

    async function deleteTuitionPackage(packageId) {
        const res = await fetchWrapper.delete(`/api/tuition/packages/${packageId}`);
        return res.json();
    }

    // Append-only tuition change-log for one student (managers only — the server enforces it).
    async function getTuitionAudit(student_id) {
        try {
            const res = await fetchWrapper.get(`/api/tuition/audit?student_id=${encodeURIComponent(student_id)}`);
            const json = await res.json();
            if (json.status === 'Success') return json.message || [];
            return [];
        } catch (e) {
            console.error('getTuitionAudit:', e);
            return [];
        }
    }

    return {
        getAllTuition, getStudentTuition,
        addTuitionRecord, updateTuitionRecord, deleteTuitionRecord,
        addTuitionPackage, updateTuitionPackage, deleteTuitionPackage,
        getTuitionAudit,
    };
}
